import EventDispatcher from '@/utils/dispatcher'
import WebSocketClient from './WebSocketClient'

export default class WebSocketService extends EventDispatcher {
  private static instance: WebSocketService
  private socketClient: WebSocketClient | null = null
  // 订阅集合
  private readonly pendingSubs = new Set<string>()
  private readonly activeSubs = new Set<string>()

  private constructor() {
    super()
  }

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService()
    }
    return WebSocketService.instance
  }

  public isConnected(): boolean {
    return this.socketClient?.isConnected
  }

  public getActiveSubs(): ReadonlySet<string> {
    return this.activeSubs
  }

  public connect(url: string): void {
    this.socketClient = new WebSocketClient(url)

    this.socketClient.addEventListener('open', (d) => {
      this.flushSubs()
      this.dispatchEvent('open', d)
    })

    this.socketClient.addEventListener('message', (d) => {
      this.dispatchEvent('message', d)
    })

    this.socketClient.addEventListener('error', (e) => this.dispatchEvent('error', e))

    this.socketClient.addEventListener('close', (c) => {
      this.dispatchEvent('close', c)
      // ★ 断线后把 activeSubs 全部转移到 pendingSubs，等待下一次 flush
      // 报价订阅问题不大，如果是开仓等要考虑是否要重发，目前是5000开仓等会超时，不会进入下次发送中
      this.requeueActiveSubs()
    })

    this.socketClient.connect()
  }

  public async destroy(_graceMs = 0): Promise<void> {
    const target = this.socketClient
    if (!target) return
    if (this.activeSubs.size > 0) this.unsubscribeAll()
    this.socketClient = null
    target.destroy(1000, 'account switch')
  }

  public send(msg: string): void {
    this.socketClient?.send(msg)
  }

  public subscribe(symbol: string): void {
    if (this.activeSubs.has(symbol) || this.pendingSubs.has(symbol)) return
    if (this.isConnected()) {
      this.send(`sub:${symbol.toUpperCase()};`)
      this.activeSubs.add(symbol)
    } else {
      this.pendingSubs.add(symbol)
    }
  }

  public batchSubscribe(symbols: readonly string[]): void {
    const list = symbols.filter((s) => !!s && !this.activeSubs.has(s) && !this.pendingSubs.has(s))
    if (list.length === 0) return

    if (this.isConnected()) {
      this.send(`sub:${list.map((s) => s.toUpperCase()).join(';')};`)
      list.forEach((s) => this.activeSubs.add(s))
    } else {
      list.forEach((s) => this.pendingSubs.add(s))
    }
  }

  public unsubscribe(symbol: string): void {
    if (this.activeSubs.has(symbol)) {
      this.send(`unsub:${symbol};`)
      this.activeSubs.delete(symbol)
    }
    // 不管是否连接，pending 一并去掉
    this.pendingSubs.delete(symbol)
  }

  public batchUnsubscribe(symbols: readonly string[]): void {
    const list = symbols.filter((s) => !!s && (this.activeSubs.has(s) || this.pendingSubs.has(s)))
    if (list.length === 0) return

    if (this.isConnected()) {
      this.send(list.map((s) => `unsub:${s};`).join(''))
    }
    list.forEach((s) => {
      this.activeSubs.delete(s)
      this.pendingSubs.delete(s)
    })
  }

  public unsubscribeAll(): void {
    this.pendingSubs.clear()
    this.activeSubs.clear()
    this.send('unsuball')
  }

  private requeueActiveSubs(): void {
    this.activeSubs.forEach((s) => this.pendingSubs.add(s))
    this.activeSubs.clear()
    // 不广播 subsChange，因为对外来说订阅仍然“有效”
  }
  private flushSubs(): void {
    if (!this.isConnected()) return
    this.pendingSubs.forEach((topic) => {
      this.send(`sub:${topic.toUpperCase()};`)
      this.activeSubs.add(topic)
    })
    this.pendingSubs.clear()
  }
}
